// ============================================================================
// CrewAhead Maritime Platform - Zustand User Store
// ============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Notification,
  SeafarerProfile,
  ShoreProfile,
  CompanyProfile,
} from '../lib/types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UserPreferences {
  /** Email notification preferences */
  emailNotifications: boolean;
  /** Push notification preferences */
  pushNotifications: boolean;
  /** SMS notification preferences */
  smsNotifications: boolean;
  /** Marketing email opt-in */
  marketingEmails: boolean;
  /** Profile visibility setting */
  profileVisibility: 'public' | 'employers_only' | 'private';
  /** UI language */
  language: string;
  /** User timezone */
  timezone: string;
  /** Preferred currency */
  currency: string;
  /** Sidebar collapsed state */
  sidebarCollapsed: boolean;
  /** Theme preference */
  theme: 'light' | 'dark' | 'system';
}

export interface UserState {
  // ─── Profile Data ─────────────────────────────────────────────────────────
  /** Cached seafarer profile (for seafarer users) */
  seafarerProfile: Partial<SeafarerProfile> | null;
  /** Cached shore profile (for shore staff users) */
  shoreProfile: Partial<ShoreProfile> | null;
  /** Cached company profile (for employer users) */
  companyProfile: Partial<CompanyProfile> | null;

  // ─── Preferences ──────────────────────────────────────────────────────────
  /** User preferences and settings */
  preferences: UserPreferences;

  // ─── Notifications ────────────────────────────────────────────────────────
  /** In-app notifications */
  notifications: Notification[];
  /** Number of unread notifications */
  unreadCount: number;

  // ─── UI State ─────────────────────────────────────────────────────────────
  /** Currently active sidebar section */
  activeSidebarSection: string | null;
  /** Currently open modal ID */
  activeModal: string | null;
  /** Toast messages queue */
  toasts: ToastMessage[];

  // ─── Actions ──────────────────────────────────────────────────────────────
  // Profile
  setProfile: (profile: Partial<SeafarerProfile | ShoreProfile | CompanyProfile>) => void;
  updateProfileField: <K extends string>(
    key: K,
    value: unknown
  ) => void;
  clearProfile: () => void;

  // Preferences
  setPreferences: (prefs: Partial<UserPreferences>) => void;
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => void;
  resetPreferences: () => void;

  // Notifications
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markNotificationRead: (notificationId: string) => void;
  markAllNotificationsRead: () => void;
  removeNotification: (notificationId: string) => void;
  clearNotifications: () => void;
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  decrementUnreadCount: () => void;

  // UI
  setActiveSidebarSection: (section: string | null) => void;
  openModal: (modalId: string) => void;
  closeModal: () => void;
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (toastId: string) => void;
  clearToasts: () => void;
}

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
}

// ─── Default Preferences ─────────────────────────────────────────────────────

const DEFAULT_PREFERENCES: UserPreferences = {
  emailNotifications: true,
  pushNotifications: true,
  smsNotifications: false,
  marketingEmails: false,
  profileVisibility: 'employers_only',
  language: 'en',
  timezone: 'UTC',
  currency: 'USD',
  sidebarCollapsed: false,
  theme: 'system',
};

// ─── User Store ──────────────────────────────────────────────────────────────

/**
 * Zustand store for user data, preferences, notifications, and UI state.
 *
 * Persists preferences to localStorage. Profile data is kept in memory
 * and synced with the server via TanStack Query.
 *
 * @example
 * ```tsx
 * const notifications = useUserStore((state) => state.notifications);
 * const addNotification = useUserStore((state) => state.addNotification);
 * const markRead = useUserStore((state) => state.markNotificationRead);
 * const preferences = useUserStore((state) => state.preferences);
 * const updatePref = useUserStore((state) => state.updatePreference);
 * ```
 */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // ─── Initial State ────────────────────────────────────────────────────
      seafarerProfile: null,
      shoreProfile: null,
      companyProfile: null,
      preferences: { ...DEFAULT_PREFERENCES },
      notifications: [],
      unreadCount: 0,
      activeSidebarSection: null,
      activeModal: null,
      toasts: [],

      // ─── Profile Actions ──────────────────────────────────────────────────

      setProfile: (profile): void => {
        // Determine which profile type based on provided fields
        if ('currentRank' in profile || 'seekingRank' in profile) {
          set({ seafarerProfile: profile as Partial<SeafarerProfile> });
        } else if ('currentPosition' in profile || 'seekingPositions' in profile) {
          set({ shoreProfile: profile as Partial<ShoreProfile> });
        } else if ('companyName' in profile || 'companyType' in profile) {
          set({ companyProfile: profile as Partial<CompanyProfile> });
        }
      },

      updateProfileField: (key, value): void => {
        const state = get();

        if (state.seafarerProfile) {
          set({
            seafarerProfile: { ...state.seafarerProfile, [key]: value },
          });
        } else if (state.shoreProfile) {
          set({
            shoreProfile: { ...state.shoreProfile, [key]: value },
          });
        } else if (state.companyProfile) {
          set({
            companyProfile: { ...state.companyProfile, [key]: value },
          });
        }
      },

      clearProfile: (): void => {
        set({
          seafarerProfile: null,
          shoreProfile: null,
          companyProfile: null,
        });
      },

      // ─── Preferences Actions ──────────────────────────────────────────────

      setPreferences: (prefs): void => {
        set({
          preferences: { ...get().preferences, ...prefs },
        });
      },

      updatePreference: (key, value): void => {
        set({
          preferences: { ...get().preferences, [key]: value },
        });
      },

      resetPreferences: (): void => {
        set({ preferences: { ...DEFAULT_PREFERENCES } });
      },

      // ─── Notification Actions ─────────────────────────────────────────────

      setNotifications: (notifications): void => {
        const unreadCount = notifications.filter((n) => !n.isRead).length;
        set({ notifications, unreadCount });
      },

      addNotification: (notification): void => {
        set((state) => ({
          notifications: [notification, ...state.notifications].slice(0, 100), // Keep last 100
          unreadCount: state.unreadCount + (notification.isRead ? 0 : 1),
        }));
      },

      markNotificationRead: (notificationId): void => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === notificationId);
          const wasUnread = notification && !notification.isRead;

          return {
            notifications: state.notifications.map((n) =>
              n.id === notificationId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            ),
            unreadCount: wasUnread
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount,
          };
        });
      },

      markAllNotificationsRead: (): void => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.isRead
              ? n
              : { ...n, isRead: true, readAt: new Date().toISOString() }
          ),
          unreadCount: 0,
        }));
      },

      removeNotification: (notificationId): void => {
        set((state) => {
          const removed = state.notifications.find((n) => n.id === notificationId);
          const wasUnread = removed && !removed.isRead;

          return {
            notifications: state.notifications.filter((n) => n.id !== notificationId),
            unreadCount: wasUnread
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount,
          };
        });
      },

      clearNotifications: (): void => {
        set({ notifications: [], unreadCount: 0 });
      },

      setUnreadCount: (count): void => {
        set({ unreadCount: Math.max(0, count) });
      },

      incrementUnreadCount: (): void => {
        set((state) => ({ unreadCount: state.unreadCount + 1 }));
      },

      decrementUnreadCount: (): void => {
        set((state) => ({ unreadCount: Math.max(0, state.unreadCount - 1) }));
      },

      // ─── UI Actions ───────────────────────────────────────────────────────

      setActiveSidebarSection: (section): void => {
        set({ activeSidebarSection: section });
      },

      openModal: (modalId): void => {
        set({ activeModal: modalId });
      },

      closeModal: (): void => {
        set({ activeModal: null });
      },

      addToast: (toast): void => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        set((state) => ({
          toasts: [...state.toasts, { ...toast, id }],
        }));

        // Auto-remove after duration
        const duration = toast.duration ?? 5000;
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },

      removeToast: (toastId): void => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== toastId),
        }));
      },

      clearToasts: (): void => {
        set({ toasts: [] });
      },
    }),
    {
      name: 'crew-ahead-user-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist preferences, sidebar state, and theme
      partialize: (state) => ({
        preferences: state.preferences,
        activeSidebarSection: state.activeSidebarSection,
      }),
    }
  )
);

// ─── Selectors ───────────────────────────────────────────────────────────────

/**
 * Selector to get the current profile (works for any user type).
 */
export const selectCurrentProfile = (
  state: UserState
): Partial<SeafarerProfile | ShoreProfile | CompanyProfile> | null => {
  return state.seafarerProfile ?? state.shoreProfile ?? state.companyProfile;
};

/**
 * Selector to get profile completeness estimate.
 */
export const selectProfileCompleteness = (state: UserState): number => {
  const profile = selectCurrentProfile(state);
  if (!profile) return 0;

  // Basic completeness check based on filled fields
  const profileEntries = Object.entries(profile).filter(
    ([key]) => key !== 'id' && key !== 'userId' && key !== 'createdAt' && key !== 'updatedAt'
  );

  if (profileEntries.length === 0) return 0;

  const filledEntries = profileEntries.filter(([, value]) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });

  return Math.round((filledEntries.length / profileEntries.length) * 100);
};

/**
 * Selector to check if user has enabled notifications.
 */
export const selectNotificationsEnabled = (state: UserState): boolean =>
  state.preferences.emailNotifications || state.preferences.pushNotifications;
